import { useAuth } from "@/contexts/AuthContext";
import { Link, useLocation } from "react-router-dom";
import { LogOut, ChevronRight, Eye, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import OfflineStatus from "@/components/layout/OfflineStatus";

const labelMap = {
  organizations: "Organization",
  "publication-houses": "Publication House",
  periodicals: "Periodical",
  "subscription-plans": "Subscription Plans",
  subscribers: "Subscribers",
  renewals: "Renewals",
  dispatch: "Dispatch",
  labels: "Labels",
  rto: "RTO",
  "tracking-import": "Tracking Import",
  "redispatch-queue": "Re-dispatch Queue",
  users: "Users & RBAC",
  new: "New",
  edit: "Edit",
};

function Breadcrumbs() {
  const { pathname } = useLocation();
  const parts = pathname.split("/").filter(Boolean);
  const crumbs = parts.map((p, i) => ({
    label: labelMap[p] || p,
    to: "/" + parts.slice(0, i + 1).join("/"),
  }));
  return (
    <nav data-testid="breadcrumbs" className="flex items-center gap-1 text-sm">
      <Link to="/" className="text-slate-500 hover:text-slate-900">Dashboard</Link>
      {crumbs.map((c, i) => (
        <span key={c.to} className="flex items-center gap-1">
          <ChevronRight size={14} className="text-slate-300" />
          <Link
            to={c.to}
            className={i === crumbs.length - 1 ? "font-medium text-slate-900" : "text-slate-500 hover:text-slate-900"}
          >
            {c.label}
          </Link>
        </span>
      ))}
    </nav>
  );
}

function TenantChip() {
  const { user } = useAuth();
  const sum = user?.scope_summary;
  if (!sum || sum.level === "global") return null;
  // Build "Org › Pub › Periodical" breadcrumb based on the deepest scope.
  const parts = [];
  if (sum.orgs?.[0]) parts.push(sum.orgs[0].name);
  if (sum.pubs?.[0] && sum.level !== "org") parts.push(sum.pubs[0].name);
  if (sum.periodicals?.[0] && sum.level === "periodical") parts.push(sum.periodicals[0].title);
  if (parts.length === 0) return null;
  return (
    <div className="hidden lg:flex items-center gap-1.5 rounded-sm border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] text-slate-600" data-testid="tenant-chip">
      <span className="font-semibold uppercase tracking-[0.18em] text-slate-400 text-[9px]">Scope</span>
      <span className="font-medium text-slate-800">{parts.join(" › ")}</span>
    </div>
  );
}

function ViewAsBanner() {
  const { isViewAsActive, exitViewAs, user } = useAuth();
  if (!isViewAsActive) return null;
  return (
    <div data-testid="view-as-banner" className="fixed left-0 right-0 top-0 z-50 flex items-center justify-center gap-3 bg-amber-500 px-4 py-1.5 text-xs font-medium text-amber-950 shadow">
      <Eye size={14} />
      <span>Viewing as <span className="font-mono font-semibold">{user?.email}</span> · read-only · all writes blocked</span>
      <button onClick={exitViewAs} className="ml-2 inline-flex items-center gap-1 rounded-sm bg-amber-950 px-2 py-0.5 text-[11px] text-amber-50 hover:bg-amber-900" data-testid="view-as-exit">
        <X size={12} /> Exit View-As
      </button>
    </div>
  );
}

export default function Topbar() {
  const { user, logout, role, isViewAsActive } = useAuth();
  const roleLabel = (role || "").replace(/_/g, " ");
  return (
    <>
      <ViewAsBanner />
      <header
        data-testid="dashboard-topbar"
        className={`sticky top-0 z-20 flex h-16 items-center justify-between border-b border-slate-200 bg-white px-6 ${isViewAsActive ? "mt-7" : ""}`}
      >
        <div className="flex items-center gap-4">
          <Breadcrumbs />
          <TenantChip />
        </div>
        <div className="flex items-center gap-4">
          <OfflineStatus />
          <div className="hidden text-right md:block">
            <div className="text-sm font-medium text-slate-900 capitalize" data-testid="topbar-user-name">
              {user?.name || "Administrator"}
              {role && <span className="ml-2 rounded-sm bg-indigo-50 px-1.5 py-0.5 text-[10px] uppercase tracking-[0.12em] text-[#002FA7]" data-testid="topbar-user-role">{roleLabel}</span>}
            </div>
            <div className="text-xs text-slate-500" data-testid="topbar-user-email">{user?.email}</div>
          </div>
          <Button variant="outline" size="sm" onClick={logout} data-testid="logout-btn" className="rounded-sm">
            <LogOut size={14} className="mr-1.5" />
            Logout
          </Button>
        </div>
      </header>
    </>
  );
}
